import { useCallback, useRef, useState } from "react"
import type { RendererProps } from "../core/element-registry"
import { useRenderContext } from "../core/render-context"
import { useAtomDefaults } from "../contexts/atom-defaults-context"
import { ElementRenderer, liftV4Props } from "./element-renderer"
import { cn } from "../lib/utils"
import {
  Sheet,
  SheetTrigger,
  SheetContent,
  SheetTitle,
} from "../components/ui/sheet"

function resolveBoolean(
  value: boolean | string | undefined,
  resolveSymbol: (value: string) => unknown,
  fallback: boolean,
): boolean {
  if (value === undefined) return fallback
  if (typeof value === "boolean") return value
  const resolved = resolveSymbol(value)
  if (typeof resolved === "boolean") return resolved
  return String(resolved ?? value).trim().toLowerCase() === "true"
}

export function SheetRenderer({ element }: RendererProps) {
  const { resolveSymbol, dispatchEvent } = useRenderContext()
  // Theme-v2 atom defaults: Sheet root is a portal-emitting provider.
  // Surface atom contract on a wrapping span.
  // Note: `element.variant` is overloaded for both open-state ("open") and
  // side placement (top/bottom/left/right); data-variant carries the raw
  // token, compatible with any of these semantic uses.
  const atomDefaults = useAtomDefaults("sheet")
  const resolvedVariant = (element.variant as string | undefined)
    ?? atomDefaults.defaultVariant
    ?? "default"
  const resolvedSize = (element as { size?: string }).size
    ?? atomDefaults.defaultSize
    ?? "default"
  const requestedOpen = element.variant === "open"
    || resolveBoolean(element.open ?? element.visible, resolveSymbol, false)
  const [isOpen, setIsOpen] = useState(requestedOpen)
  const previousElementRef = useRef(element)
  const text = element.text ? String(resolveSymbol(element.text) ?? "") : undefined

  if (previousElementRef.current !== element) {
    previousElementRef.current = element
    if (isOpen !== requestedOpen) setIsOpen(requestedOpen)
  }

  const side = (() => {
    const v = element.side ?? element.variant
    if (v === "top" || v === "bottom" || v === "left" || v === "right") return v
    return "bottom"
  })()

  const handleOpenChange = useCallback(
    (open: boolean) => {
      setIsOpen(open)
      if (!open) {
        dispatchEvent(element.id, "close", {})
      } else {
        dispatchEvent(element.id, "open", {})
      }
    },
    [element.id, dispatchEvent],
  )

  // ── trigger / content slots ────────────────────────────────────────────────
  // Until this existed EVERY child was mapped into <SheetContent>, so a block
  // authoring the conventional `[trigger, content]` pair buried its trigger
  // inside the closed panel and nothing on screen could open the sheet. The
  // shape below is popover-renderer.tsx's: type-match first, positional
  // fallback second.
  //
  // Why popover's shape and not the dialog's typed-slots-only shape: the
  // dialog pair works because `dialog-trigger` / `dialog-content` are real
  // element types in packages/cogo-validator/ontology/element-types.json with
  // registered renderers. `sheet-trigger` / `sheet-content` are not, and
  // admitting them is not a local edit — element-types.json is sha256-pinned
  // beside its type count (cogo-validator
  // __tests__/element-bind-ontology-contract.test.ts, the sole pin) and that
  // count is re-derived across cogo-validator / cogo-react-renderer /
  // cogo-react-native-renderer / cogo-kg-browser / cogo-headless /
  // cogo-prompt-engine, plus a regenerated ui-elements-v1 RDF shard. A block
  // that authored those types today would take an `element-type-unknown`
  // safety error instead of rendering. So sheet joins the sibling overlay
  // whose slots are likewise unadmitted, and the type-match arm is the seam
  // that goes live the day the pair is admitted.
  const allChildren = element.children ?? []
  const rawContentWrapper = allChildren.find((child) => child.type === "sheet-content")
  const rawTrigger =
    allChildren.find((child) => child.type === "sheet-trigger")
    // Positional fallback, guarded on something being LEFT for the panel.
    // Popover's unguarded `?? allChildren[0]` hoists a sole content child out
    // of the overlay, which would empty the sheet rather than give it a way to
    // open; a one-child sheet is all content and keeps today's behaviour.
    ?? (allChildren.length > 1
      ? allChildren.find((child) => child !== rawContentWrapper)
      : undefined)
  const contentWrapper = rawContentWrapper ? liftV4Props(rawContentWrapper) : undefined
  const triggerChild = rawTrigger ? liftV4Props(rawTrigger) : undefined
  // Lossless: the wrapper's children ARE the panel, and anything else the
  // author wrote beside it (other than the trigger) follows them rather than
  // vanishing. Same invariant popover-renderer.tsx states — trigger and panel
  // are disjoint, and every authored child lands in exactly one of them.
  //
  // The sibling filter compares against `rawContentWrapper`, NOT the lifted
  // `contentWrapper`. `liftV4Props` copies ONLY when the element carries a
  // `props` wrapper (element-renderer.tsx:1050-1052) and otherwise returns the
  // same reference — so comparing against the lifted value is correct by
  // accident for props-less wrappers and re-emits the wrapper inside its own
  // panel for the rest. The covering test authors `props: {}` for that reason.
  const sheetBody = contentWrapper
    ? [
        ...(contentWrapper.children ?? []),
        ...allChildren.filter((child) => child !== rawContentWrapper && child !== rawTrigger),
      ]
    : allChildren.filter((child) => child !== rawTrigger)
  const sheetTitle = text
    ?? (contentWrapper?.text ? String(resolveSymbol(contentWrapper.text) ?? "") : undefined)
  // A typed slot is a wrapper — render what it wraps. A positionally-picked
  // child is a real element (the authored button) — render the element, so its
  // icon/variant/interaction survive instead of being flattened to text.
  const triggerIsTypedSlot = triggerChild?.type === "sheet-trigger"
  const triggerContent = !triggerChild
    ? null
    : triggerIsTypedSlot
      ? (triggerChild.children ?? []).map((child) => (
          <ElementRenderer key={child.id} element={child} />
        ))
      : <ElementRenderer element={triggerChild} />
  // Identity goes on the trigger ONLY for the typed slot, whose id nothing else
  // renders. A positionally-picked child renders through ElementRenderer, which
  // already emits that child's `data-cogo-element-id` on the node that carries
  // its box — stamping it here too would give one id two nodes, and every
  // consumer of the identity contract resolves an id by querySelector.
  const triggerIdentity = triggerIsTypedSlot
    ? { "data-cogo-element-id": triggerChild.id, "data-cogo-type": triggerChild.type }
    : {}

  return (
    <span
      data-atom-type="sheet"
      data-variant={resolvedVariant}
      data-size={resolvedSize}
      data-side={side}
      data-is-open={isOpen ? "true" : "false"}
      data-testid={`sheet-${element.id}`}
      aria-expanded={isOpen}
      className={element.className ? cn("inline-block", element.className) : undefined}
    >
      <Sheet open={isOpen} onOpenChange={handleOpenChange}>
        {triggerChild && (
          <SheetTrigger
            nativeButton={false}
            className="cn-sheet-trigger cursor-pointer"
            data-testid={`sheet-trigger-${element.id}`}
            {...triggerIdentity}
            render={<div>{triggerContent}</div>}
          />
        )}
        <SheetContent
          side={side}
          className={cn(
            contentWrapper?.className ?? element.className,
          )}
          data-testid={`sheet-content-${element.id}`}
        >
          {sheetTitle && (
            <SheetTitle>
              {sheetTitle}
            </SheetTitle>
          )}
          {sheetBody.map((child) => (
            <ElementRenderer key={child.id} element={child} />
          ))}
        </SheetContent>
      </Sheet>
    </span>
  )
}
